import AppIcon from '@/components/content/app/AppIcon'
import { getBrandColors, isPopulatedObject, cn } from '@/lib/utils'
import type { App, Media } from '@/payload-types'

type StackIconProps = React.HTMLAttributes<HTMLDivElement> & {
  apps?: App[]
}

export default function StackIcon({ apps }: StackIconProps) {
  const visibleApps = (apps ?? []).filter(Boolean) as App[]

  const SPEED = 25
  const APP_HEIGHT = 16
  const GAP = 6

  const minApps = 4
  const actualApps = visibleApps?.length ?? 0
  const targetApps = Math.max(minApps, Math.ceil(actualApps / 2) * 2)
  const emptyAppsNeeded = targetApps - actualApps
  const singleSetSize = (targetApps * APP_HEIGHT + (targetApps - 1) * GAP) / 2
  const duration = singleSetSize / SPEED

  const renderApps = () => {
    return (
      <>
        {visibleApps?.map((app, index) => {
          const appIcon = app?.icon as Media
          const appGlyph = app?.glyph as Media
          const { backgroundColor } = getBrandColors(app)

          if (!isPopulatedObject<Media>(appIcon) && !isPopulatedObject<Media>(appGlyph)) {
            return null
          }
          return (
            app && (
              <AppIcon
                key={`${app.id}-${index}`}
                icon={appIcon}
                glyph={appGlyph}
                name={app.name}
                background={backgroundColor}
                size={16}
              />
            )
          )
        })}
        {emptyAppsNeeded > 0 &&
          Array.from({ length: emptyAppsNeeded }).map((_, index) => (
            <EmptyIcon key={`empty-${index}`} />
          ))}
      </>
    )
  }

  return (
    <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded bg-gradient-to-b from-black/0 via-black/0 to-black/10 px-2 shadow-[inset_0px_0px_0px_1px_rgba(0,0,0,0.06)] outline outline-[3px] outline-offset-[-3px] outline-black/10">
      <div className="relative top-[3px] h-[50px] overflow-hidden">
        <div
          className={cn(
            'flex flex-wrap items-center justify-center gap-1.5',
            visibleApps.length > 4
              ? 'pause group-hover:play animate-marquee-vertical transition-[animation-play-state] duration-300'
              : 'py-1.5',
          )}
          style={
            {
              '--duration': `${duration}s`,
              '--single-set-size': `${singleSetSize}px`,
              '--offset-start': `${GAP}px`,
              '--offset-end': `-${GAP / 2}px`,
            } as React.CSSProperties
          }
        >
          {renderApps()}
          {visibleApps.length > 4 && renderApps()}
        </div>
      </div>
    </div>
  )
}

export function EmptyIcon() {
  return <div className="h-4 w-4 rounded-key border-[0.75px] border-black/10 bg-black/5"></div>
}
